import { Box, Typography } from "@mui/material"
import type { ChangeEvent, FC } from "react"
import { useState } from "react"

import { TabletWarehouseFilter } from "../Incoming/components"

import { GoodsReceiptPalletDialog } from "./GoodsReceiptPalletDialog"

import SearchInput from "@/components/SearchInput/SearchInput"
import type { GoodsReceiptPallet } from "@/graphql/codegen/graphql"
import { useStoredPalletScanning } from "@/screens/Tablet/hooks/useStoredPalletScanning"
import { useGoodsReceiptPalletActions } from "@/screens/WarehouseReceiving/hooks/useGoodsReceiptPalletActions"
import { StoredGoodsReceiptPalletsTable } from "@/screens/WarehouseReceiving/subfeatures/Stored/components"
import { useStoredGoodsReceiptPalletsTable } from "@/screens/WarehouseReceiving/subfeatures/Stored/hooks"
import { isAkrochemManagedWarehouse } from "@/screens/WarehouseReceiving/utils"

export const TabletStoredGoodsReceiptsContainer: FC = () => {
  const [selectedPallet, setSelectedPallet] = useState<GoodsReceiptPallet | null>(null)
  const [isPalletDialogOpen, setIsPalletDialogOpen] = useState(false)

  const {
    goodsReceiptPallets,
    isGoodsReceiptPalletsFetching,
    pageInfo,
    searchQuery,
    warehouseAreaIds,
    sortingState,
    tableContainerRef,
    handleSearchChange,
    handleFilterChange,
    handleSortingChange,
    handleScroll,
  } = useStoredGoodsReceiptPalletsTable({
    routePath: "/app/tablet/warehouse-receiving",
  })

  const { handleQrPrint } = useGoodsReceiptPalletActions()

  useStoredPalletScanning({
    onPalletScanned: (pallet) => {
      setIsPalletDialogOpen(false)
      setSelectedPallet(pallet)
      setIsPalletDialogOpen(true)
    },
  })

  const handleRowClick = (pallet: GoodsReceiptPallet) => {
    if (!isAkrochemManagedWarehouse(pallet.goodsReceiptPalletRow?.goodsReceipt?.warehouseArea?.type)) {
      return
    }

    setSelectedPallet(pallet)
    setIsPalletDialogOpen(true)
  }

  const handlePalletDialogClose = () => {
    setIsPalletDialogOpen(false)
    setSelectedPallet(null)
  }

  return (
    <Box className='flex h-full flex-col gap-y-6 p-6'>
      <SearchInput
        placeholder='Search by Product ID or Reference #'
        defaultValue={searchQuery}
        onChange={(e: ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
        className='w-full'
      />

      <Box className='flex items-center justify-between'>
        <Typography variant='h6' className='font-bold text-gray-700'>
          {pageInfo?.total ?? 0} Stored
        </Typography>
        <TabletWarehouseFilter
          selectedValue={warehouseAreaIds?.[0] || null}
          onChange={(warehouseId) => handleFilterChange(warehouseId ? [warehouseId] : [])}
        />
      </Box>

      <Box className='flex min-h-0 flex-1 overflow-hidden'>
        <StoredGoodsReceiptPalletsTable
          goodsReceiptPallets={goodsReceiptPallets}
          isLoading={isGoodsReceiptPalletsFetching}
          sortingState={sortingState}
          tableContainerRef={tableContainerRef}
          onSortingChange={handleSortingChange}
          onQrPrint={handleQrPrint}
          onRowClick={handleRowClick}
          onScroll={handleScroll}
        />
      </Box>

      {isPalletDialogOpen && selectedPallet && (
        <GoodsReceiptPalletDialog pallet={selectedPallet} onClose={handlePalletDialogClose} onQrPrint={handleQrPrint} />
      )}
    </Box>
  )
}
